import { useState, useEffect, useCallback } from "react";
import type { Event } from "@/types/event";
import {
	fetchEvents,
	createEvent,
	updateEvent,
	deleteEvent,
} from "@/services/eventService";

interface UseEventsReturn {
	events: Event[] | null;
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
	createNewEvent: (
		event: Omit<Event, "id" | "createdAt" | "updatedAt">,
	) => Promise<void>;
	updateExistingEvent: (id: string, event: Partial<Event>) => Promise<void>;
	deleteExistingEvent: (id: string) => Promise<void>;
}

export const useEvents = (): UseEventsReturn => {
	const [events, setEvents] = useState<Event[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchEventsList = useCallback(async () => {
		try {
			setLoading(true);
			const data = await fetchEvents();
			setEvents(data);
			setError(null);
		} catch (err) {
			console.error("イベント取得エラー:", err);
			setError("イベントの取得に失敗しました");
		} finally {
			setLoading(false);
		}
	}, []); // 依存配列は空（外部の関数は不変）

	const createNewEvent = useCallback(
		async (event: Omit<Event, "id" | "createdAt" | "updatedAt">) => {
			try {
				setLoading(true);
				await createEvent(event);
				await fetchEventsList();
			} catch (err) {
				console.error("イベント作成エラー:", err);
				throw err;
			}
		},
		[fetchEventsList],
	);

	const updateExistingEvent = useCallback(
		async (id: string, event: Partial<Event>) => {
			try {
				setLoading(true);
				await updateEvent(id, event);
				await fetchEventsList();
			} catch (err) {
				console.error("イベント更新エラー:", err);
				throw err;
			}
		},
		[fetchEventsList],
	);

	const deleteExistingEvent = useCallback(
		async (id: string) => {
			try {
				setLoading(true);
				await deleteEvent(id);
				await fetchEventsList();
			} catch (err) {
				console.error("イベント削除エラー:", err);
				throw err;
			}
		},
		[fetchEventsList],
	);

	useEffect(() => {
		fetchEventsList();
	}, [fetchEventsList]); // fetchEventsListを依存配列に追加

	return {
		events,
		loading,
		error,
		refetch: fetchEventsList,
		createNewEvent,
		updateExistingEvent,
		deleteExistingEvent,
	};
};
